import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { calculateAll, type CipherResult, tripleNumbers } from '@gematria/shared'

const API_URL = import.meta.env.PROD ? '' : 'http://localhost:3001'

// Cipher colors for preview
const cipherColors: Record<string, { name: string; value: string; bg: string }> = {
  'Alphanumeric Qabbala': { name: 'text-stone-300', value: 'text-stone-100', bg: 'bg-stone-900' },
  'AQ Squares': { name: 'text-[#B89B6B]', value: 'text-[#D4B896]', bg: 'bg-[#2A2015]' },
  'AQ Primes': { name: 'text-[#D4B896]', value: 'text-[#EBD4B0]', bg: 'bg-[#352A1F]' },
  'AQ Trigonal': { name: 'text-[#EBD4B0]', value: 'text-[#F5E6D0]', bg: 'bg-[#403526]' },
  'Ordinal': { name: 'text-stone-200', value: 'text-stone-100', bg: 'bg-stone-700' },
  'Numeric QWERTY': { name: 'text-green-400', value: 'text-green-200', bg: 'bg-green-950' },
  'NQ Squares': { name: 'text-[#5A9B5A]', value: 'text-[#7ABD7A]', bg: 'bg-[#0F2510]' },
  'NQ Primes': { name: 'text-[#6ABD6A]', value: 'text-[#8BDF8B]', bg: 'bg-[#1A351A]' },
  'NQ Trigonal': { name: 'text-[#8BDF8B]', value: 'text-[#ABFFAB]', bg: 'bg-[#2A452A]' },
  'QWERTY': { name: 'text-lime-400', value: 'text-lime-200', bg: 'bg-lime-950' },
  'Standard': { name: 'text-[#E8A830]', value: 'text-[#F8C040]', bg: 'bg-[#3D2815]' },
  'Satanic': { name: 'text-red-500', value: 'text-red-300', bg: 'bg-red-950' },
  'Synx': { name: 'text-cyan-400', value: 'text-cyan-200', bg: 'bg-cyan-950' },
  'Reduction': { name: 'text-[#F0B840]', value: 'text-[#F8D060]', bg: 'bg-[#4A3220]' },
  'Vaxen': { name: 'text-violet-400', value: 'text-violet-200', bg: 'bg-violet-950' },
  'Unun': { name: 'text-indigo-400', value: 'text-indigo-200', bg: 'bg-indigo-950' },
  'Based Atlanteanism': { name: 'text-amber-500', value: 'text-amber-300', bg: 'bg-amber-950' },
}

const cipherDisplayNames: Record<string, string> = {
  'Alphanumeric Qabbala': 'AQ',
  'Numeric QWERTY': 'NQ',
}

interface User {
  id: string
  username: string
}

interface SettingsPageProps {
  user: User | null
}

export function SettingsPage({ user }: SettingsPageProps) {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [bio, setBio] = useState('')
  const [pinnedPhraseText, setPinnedPhraseText] = useState('')
  const [pinnedPhraseResults, setPinnedPhraseResults] = useState<CipherResult[]>([])
  const [saveMessage, setSaveMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (!user) {
      navigate('/')
      return
    }
    fetchProfile()
  }, [user])

  // Calculate results when pinned phrase text changes
  useEffect(() => {
    if (pinnedPhraseText.trim()) {
      const results = calculateAll(pinnedPhraseText)
      setPinnedPhraseResults(results)
    } else {
      setPinnedPhraseResults([])
    }
  }, [pinnedPhraseText])

  const fetchProfile = async () => {
    try {
      const session = localStorage.getItem('session')
      if (!session) return

      const response = await fetch(`${API_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${session}` }
      })

      if (response.ok) {
        const data = await response.json()
        setBio(data.bio || '')
        if (data.pinned_phrase) {
          setPinnedPhraseText(data.pinned_phrase.phrase)
        setPinnedPhraseResults(data.pinned_phrase.results || [])
      }
    }
    } catch (error) {
      console.error('Failed to fetch profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveMessage('')
    setErrorMessage('')

    try {
      const session = localStorage.getItem('session')
      if (!session) {
        setErrorMessage('Not authenticated')
        return
      }

      const csrfToken = localStorage.getItem('csrfToken')
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session}`,
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          bio: bio.trim(),
          pinned_phrase: pinnedPhraseText.trim() ? {
            phrase: pinnedPhraseText.trim(),
            results: pinnedPhraseResults
          } : null
        })
      })

      if (response.ok) {
        setSaveMessage('Profile saved successfully!')
        setTimeout(() => setSaveMessage(''), 3000)
      } else {
        const error = await response.json()
        setErrorMessage(error.error || 'Failed to save profile')
      }
    } catch (error) {
      setErrorMessage('Network error. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ fontFamily: "'Special Elite', cursive" }}>
        <div className="text-center">
          <p className="text-green-600 mb-4">Please sign in to access settings</p>
          <Link to="/" className="text-green-400 hover:text-green-300 underline">
            Return to Calculator
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Special Elite', cursive" }}>
      {/* Header */}
      <header className="border-b border-green-900 bg-black/50 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/profile" className="text-lg font-bold text-green-400 hover:text-green-300 transition-colors">
                ← Vault
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => {
                  localStorage.removeItem('session')
                  localStorage.removeItem('csrfToken')
                  localStorage.removeItem('user')
                  navigate('/')
                }}
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-green-600">Loading...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Bio Section */}
            <div className="bg-green-950/20 border border-green-800 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 !text-green-400" style={{ fontFamily: "'Special Elite', cursive" }}>Bio</h2>
              <p className="text-sm text-green-600 mb-3">
                This bio text will be displayed on your public profile page.
              </p>
              <textarea
                value={bio}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setBio(e.target.value)
                  }
                }}
                placeholder="Tell others about yourself..."
                className="w-full h-24 px-4 py-3 bg-black border border-green-700 rounded-lg focus:border-green-400 focus:outline-none text-green-400 resize-none"
              />
              <div className="flex justify-between mt-2 text-xs text-green-600">
                <span>{bio.length}/100 characters</span>
                {bio.length >= 100 && <span className="text-amber-400">Maximum reached</span>}
              </div>
            </div>

            {/* Pinned Phrase */}
            <div className="bg-green-950/20 border border-green-800 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 !text-green-400" style={{ fontFamily: "'Special Elite', cursive" }}>Pinned Phrase</h2>
              <p className="text-sm text-green-600 mb-3">
                Type any phrase to display on your public profile. All cipher values will be shown.
              </p>
              <input
                type="text"
                value={pinnedPhraseText}
                onChange={(e) => {
                  if (e.target.value.length <= 80) {
                    setPinnedPhraseText(e.target.value)
                  }
                }}
                placeholder="Enter a phrase..."
                className="w-full px-4 py-3 bg-black border border-green-700 rounded-lg focus:border-green-400 focus:outline-none text-green-400 mb-2"
              />
              <div className="flex justify-between text-xs text-green-600">
                <span>{pinnedPhraseText.length}/80 characters</span>
                {pinnedPhraseText.length >= 80 && <span className="text-amber-400">Maximum reached</span>}
              </div>
              
              {/* Preview */}
              {pinnedPhraseText.trim() && pinnedPhraseResults.length > 0 && (
                <div className="mt-4 p-4 bg-black/50 rounded-lg">
                  <p className="text-sm text-green-600 mb-2">Preview:</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {pinnedPhraseResults.map((result, idx) => {
                      const colors = cipherColors[result.name]
                      const displayName = cipherDisplayNames[result.name] || result.name
                      const isTriple = tripleNumbers.includes(result.value)
                      return (
                        <span key={idx}>
                          <span className={colors.name}>{displayName}:</span>{' '}
                          <span 
                            className={`font-mono font-semibold ${isTriple ? 'text-amber-400' : colors.value}`}
                            style={isTriple ? { textShadow: '0 0 8px rgba(251, 191, 36, 0.8), 0 0 16px rgba(251, 191, 36, 0.4)' } : undefined}
                          >{result.value}</span>
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Messages */}
            {saveMessage && (
              <div className="bg-green-900/50 border border-green-600 text-green-400 px-4 py-3 rounded-lg">
                {saveMessage}
              </div>
            )}
            {errorMessage && (
              <div className="bg-red-900/50 border border-red-600 text-red-400 px-4 py-3 rounded-lg">
                {errorMessage}
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 disabled:from-green-900 disabled:to-emerald-900 disabled:text-green-700 text-black font-bold rounded-lg transition-all shadow-lg shadow-green-500/20"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

            {/* Public Profile Link */}
            <div className="text-center pt-4 border-t border-green-800">
              <p className="text-sm text-green-600 mb-2">Your public profile URL:</p>
              <p className="text-green-400 font-mono text-sm">{window.location.origin}/u/{user.username}</p>
              <Link 
                to={`/u/${user.username}`}
                className="inline-block mt-2 text-sm text-green-400 hover:text-green-300 underline"
              >
                View your public profile →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
